import type {
  AssetsApprovalSchemaNullable,
  AssetsShareElasticSchemaWithIdNullable,
} from '@iconik-io/iconik-api/src/services/assets';

import ContextMenu from '../../components/context_menu/context_menu';
import contextMenuConnector from '../../components/context_menu/context_menu_connector';
import type {
  ContextMenuItemDefinition,
  GetDefinition,
} from '../../components/context_menu/types';
import selectMergedPermissions from '../../components/permissions_manager/selectors';
import { DELETE_SHARES_MODAL } from '../../constants/modals';
import { selectCurrentAssetReviewRequest } from '../../ducks/asset_review_approvals/selectors';
import { isCommonAclsPrefix } from '../../ducks/common_acls/types';
import { historyPush, windowLocationChange } from '../../ducks/history/actions';
import { openModal } from '../../ducks/modals_manager/slice';
import { ICONS } from '../../icons/icon';
import type { AppDispatch } from '../../store/types';
import { selectUserRoles } from '../../utils/get_user_roles';

const getDefinition: GetDefinition<AssetsShareElasticSchemaWithIdNullable> = (
  props,
  state,
) => {
  const [contextItem] = props.contextItems;
  const userData = state.authReducer.userData;

  const disabledDeleteOption =
    userData.id !== contextItem?.owner_id && !userData.is_admin;
  const items: ContextMenuItemDefinition[] = [];

  if (!contextItem || !contextItem.object_type) {
    return { items };
  }

  // A share's object_type is typed as a plain string, and the endpoint that
  // would narrow it is BE-2419. Reading state.acls with anything outside the
  // known prefixes throws, so an unknown type gets no write permission.
  // https://linear.app/iconik/issue/BE-2419
  const hasObjectWritePermission = isCommonAclsPrefix(contextItem.object_type)
    ? selectMergedPermissions(
        state,
        contextItem.object_type,
        contextItem.object_id,
      )?.write
    : false;

  const userRoles = selectUserRoles(state);
  let reviewRequest: AssetsApprovalSchemaNullable | undefined;
  const assetId = state.pages.asset.assetId;
  if (assetId) {
    const currentReviewRequest = selectCurrentAssetReviewRequest(
      state,
      assetId,
    );
    const reviewShareId = currentReviewRequest?.share_id;
    if (reviewShareId === contextItem.id) {
      reviewRequest = currentReviewRequest;
    }
  }

  const shareUrl = contextItem.personal_url ?? contextItem.url;

  items.push({
    label: 'Visit Share',
    action: () => (dispatch: AppDispatch) => {
      if (shareUrl) {
        dispatch(windowLocationChange(shareUrl));
      }
    },
    icon: ICONS.GoToPage,
    disabled: !shareUrl,
    helpMessage: !shareUrl
      ? 'You are not a participant of this share.'
      : undefined,
  });

  if (userRoles.canDeleteObjectShares || userRoles.canManageAllShares) {
    items.push({
      label: 'Delete Share…',
      action: () => (dispatch: AppDispatch) =>
        dispatch(
          openModal({
            id: DELETE_SHARES_MODAL,
            contextItems: [
              { ...contextItem, is_approval: reviewRequest !== undefined },
            ],
          }),
        ),
      icon: ICONS.TrashBin,
      disabled: disabledDeleteOption,
      helpMessage:
        disabledDeleteOption && userRoles.canManageAllShares
          ? 'Deleting other user’s shares is not allowed from the Shares panel.'
          : undefined,
    });
  }

  if (userRoles.canWriteShares || userRoles.canManageAllShares) {
    items.push({
      label: 'Edit Share',
      action: () => (dispatch: AppDispatch) => {
        dispatch(
          historyPush(`/admin/share_management/shares/${contextItem.id}`),
        );
      },
      icon: ICONS.Edit,
      disabled:
        (!userData.is_admin &&
          userData.id !== contextItem.owner_id &&
          !hasObjectWritePermission) ||
        !contextItem,
    });
  }
  return {
    items,
  };
};

const SharePanelContextMenu = contextMenuConnector(getDefinition)(ContextMenu);

export default SharePanelContextMenu;
